import { gsap, SplitText, EASE } from '../../lib/gsap';

type LineRevealOptions = {
	/** ScrollTrigger start of the reveal, relative to the heading. */
	start?: string;
	/** Delay before the first line moves, in seconds. */
	delay?: number;
};

/**
 * Masked line reveal for a display heading (Vision and Contact).
 *
 * Call it from a gsap.matchMedia() MOTION_OK branch and pass that branch's context.
 * The heading is hidden synchronously (useGSAP runs before paint), split into its
 * rendered lines once the web fonts are ready, then each line rises out of its mask
 * when the heading enters the viewport. The split is created inside the context, so
 * reverting it restores the original markup; autoSplit re-measures lines on resize.
 * Returns a cleanup that cancels the pending font wait.
 */
export function revealLines(
	heading: HTMLElement,
	context: gsap.Context,
	{ start = 'top 80%', delay = 0 }: LineRevealOptions = {},
): () => void {
	let cancelled = false;
	gsap.set(heading, { autoAlpha: 0 });

	document.fonts.ready.then(() => {
		if (cancelled) return;
		context.add(() => {
			gsap.set(heading, { autoAlpha: 1 });
			SplitText.create(heading, {
				type: 'lines',
				mask: 'lines',
				autoSplit: true,
				// The default whitespace reduction turns the French non-breaking spaces
				// (before ? ! : ;) into plain spaces, which orphans the punctuation.
				reduceWhiteSpace: false,
				onSplit: (split: SplitText) => {
					// Tight display leading would clip descenders (g, p, q) and accents inside
					// the masks: extend each mask without changing the layout.
					gsap.set(split.masks, {
						paddingTop: '0.06em',
						marginTop: '-0.06em',
						paddingBottom: '0.16em',
						marginBottom: '-0.16em',
					});
					return gsap.from(split.lines, {
						yPercent: 112,
						duration: 1.15,
						delay,
						ease: EASE,
						stagger: 0.08,
						scrollTrigger: { trigger: heading, start },
					});
				},
			});
		});
	});

	return () => {
		cancelled = true;
	};
}
